import type { ReactNode } from "react";
import { mortgageValue, unmortgageCost } from "@grundbesitz/engine";
import { BOARD, GO_SALARY, JAIL_FINE } from "@grundbesitz/shared";
import type { PlayerAction, RoomView } from "@grundbesitz/shared";
import { formatMoney } from "../../lib/format";
import { availability, isSpectating } from "../../lib/gameView";
import { PropertyCard } from "../board/PropertyCard";
import { Button } from "../ui/Button";
import { Sheet } from "../ui/Sheet";
import { useAct } from "./hooks";
import { useUiStore } from "./uiStore";

const SPECIAL_TEXT: Record<string, string> = {
  GO: `Wer LOS erreicht oder passiert, erhält ${formatMoney(GO_SALARY)}.`,
  JAIL: "Zu Besuch passiert hier nichts. Wer verhaftet wurde, sitzt hier.",
  FREE_PARKING: "Hier passiert nichts.",
  GO_TO_JAIL: "Wer hier landet, geht direkt ins Gefängnis, ohne LOS-Geld.",
  CHANCE: "Hier wird eine Ereigniskarte gezogen und sofort ausgeführt.",
  COMMUNITY: "Hier wird eine Gemeinschaftskarte gezogen und sofort ausgeführt.",
};

function QuickAction({
  label,
  reason,
  disabled,
  onClick,
}: {
  label: ReactNode;
  reason: string | null;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <div>
      <Button className="w-full" disabled={disabled} title={reason ?? undefined} onClick={onClick}>
        {label}
      </Button>
      {reason && <p className="mt-1 text-xs text-ink-2">{reason}</p>}
    </div>
  );
}

/** Field card in a sheet. For the viewer's own properties it adds the short build and mortgage actions. */
export function FieldSheet({ room }: { room: RoomView }) {
  const selected = useUiStore((s) => s.selectedField);
  const selectField = useUiStore((s) => s.selectField);
  const openTab = useUiStore((s) => s.openTab);
  const openTradeWith = useUiStore((s) => s.openTradeWith);
  const { run, busy } = useAct();
  const game = room.game;
  if (!game || selected === null) return null;

  const field = BOARD[selected];
  const close = () => selectField(null);
  const selfId = room.you.playerId;
  const spectating = isSpectating(room);
  const ownable = field.kind === "STREET" || field.kind === "RAILROAD" || field.kind === "UTILITY";
  const prop = ownable ? game.properties[selected] : null;
  const mine = !spectating && prop?.owner === selfId;
  const foreignOwner = !spectating && prop?.owner && prop.owner !== selfId ? game.players.find((p) => p.id === prop.owner) : undefined;

  const act = (action: PlayerAction) => () => {
    void run(action);
  };
  const quick = (action: PlayerAction, label: ReactNode) => {
    const a = availability(game, selfId, action);
    return <QuickAction label={label} disabled={busy || !a.ok} reason={a.ok ? null : a.reason} onClick={act(action)} />;
  };

  let special: string | undefined;
  if (field.kind === "TAX") special = `Steuerfeld: ${formatMoney(field.amount)} an die Bank.`;
  else if (!ownable) special = SPECIAL_TEXT[field.kind];
  if (field.kind === "JAIL") special += ` Die Kaution beträgt ${formatMoney(JAIL_FINE)}.`;

  return (
    <Sheet open onClose={close} title={field.name}>
      <div className="space-y-4">
        {ownable ? (
          <PropertyCard field={selected} game={game} members={room.members} />
        ) : (
          <p className="text-sm leading-relaxed text-ink-2">{special}</p>
        )}

        {mine && prop && (
          <div className="space-y-3 border-t border-line pt-4">
            <p className="text-sm font-semibold">Deine Aktionen</p>
            {field.kind === "STREET" && (
              <div className="grid gap-3 sm:grid-cols-2">
                {quick({ type: "BUILD", field: selected }, `${prop.buildings === 4 ? "Hotel" : "Haus"} bauen (${formatMoney(field.houseCost)})`)}
                {prop.buildings > 0 && quick({ type: "SELL_BUILDING", field: selected }, `Gebäude verkaufen (${formatMoney(field.houseCost / 2)})`)}
              </div>
            )}
            {prop.mortgaged
              ? quick({ type: "UNMORTGAGE", field: selected }, `Hypothek ablösen (${formatMoney(unmortgageCost(selected))})`)
              : quick({ type: "MORTGAGE", field: selected }, `Hypothek aufnehmen (${formatMoney(mortgageValue(selected))})`)}
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                close();
                openTab("assets");
              }}
            >
              Besitz verwalten
            </Button>
          </div>
        )}

        {foreignOwner && (
          <div className="border-t border-line pt-4">
            <Button variant="primary" className="w-full" onClick={() => openTradeWith(foreignOwner.id)}>
              Handel mit {foreignOwner.name} anbieten
            </Button>
          </div>
        )}
      </div>
    </Sheet>
  );
}
